import * as Sentry from '@sentry/browser';
import { BalanceManagerApiService } from '@/services/api';

/* eslint-disable camelcase */
type balanceType = {
  open_balance: string;
  total_paid: string;
  financing_initial_value: string;
  financing_total_value: string;
};

export type contractDataType = {
  contractId: string;
  contractualConditionId: string;
  hasIpcaAdjustment: boolean;
  enrollmentId: string;
};

type contractType = {
  id: string;
  enrollment_id: string;
  contractual_condition: {
    id: string;
    payment_method_id: string;
    churn_balance_monthly_interest_rate: number;
    churn_repayment_monthly_interest_rate: number;
    churn_late_fee: number;
    financing_interest_value: number;
    financing_principal_value: number;
    financing_maturity: number;
    enterprise: string;
    installment_value: number;
    ipca_adjustment: false;
    early_settlement_interests: string;
    tribe_ids: string[];
  };
};

type fetchBalanceType = {
  getBalanceData: (
    contractId: string,
  ) => Promise<balanceType | Record<string, never>>;
  getContractData: () => Promise<contractDataType | undefined>;
};

function fetchBalance(): fetchBalanceType {
  const { api } = BalanceManagerApiService();

  async function getBalanceData(
    contractId: string,
  ): Promise<balanceType | Record<string, never>> {
    try {
      const { data } = await api.get<balanceType>(
        `/external/v1/statements/${contractId}/balance`,
      );

      return data;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: `Attempt to retrieve balance data ended in failure, possibly due to the absence of the contract's id`,
        },
      });
    }

    return {};
  }

  async function getContractData(): Promise<contractDataType | undefined> {
    try {
      const { data } = await api.get<contractType>(`/external/v1/contracts`);
      const contractData: contractDataType = {
        contractId: data.id,
        contractualConditionId: data.contractual_condition.id,
        hasIpcaAdjustment: data.contractual_condition.ipca_adjustment,
        enrollmentId: data.enrollment_id,
      };

      return contractData;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: `Attempt to retrieve the contract's data ended in failure`,
        },
      });
    }

    return undefined;
  }

  return { getBalanceData, getContractData };
}

export default fetchBalance;
